import type { ReactNode } from "react";
import type { GraphqlGlossaryListItem } from "@noiman/shared-graphql";
import { LearnCard, learnLevelWord } from "@/components/learn/LearnCard";
import { TechMark } from "@noiman/ui/tech-mark";
import { LgBadge, LgCardScope, LgLevel, LgTag } from "@/components/luagate-ui";
import { getGlossaryTopic, type GlossaryTopicSlug, type GlossaryTopicSource } from "@/lib/glossary-topics";
import { getGlossaryVisual } from "@/lib/glossary-visuals";
import { GlossaryVisualThumb } from "@/components/glossary/GlossaryVisualPicture";
import { TOPIC_ART } from "@/lib/glossary-topic-art";
import { GlossaryArt } from "@/components/glossary/GlossaryArt";
import { GLOSSARY_ART_IMAGES } from "@/lib/glossary-art-images.generated";

/**
 * 領域の札の絵の窓に入れる中身。図の無い用語の窓にも同じ絵を置き、札の高さをそろえる
 */
export function GlossaryTopicArt({ slug }: { slug: GlossaryTopicSlug }) {
  return (
    <img
      src={`/course-thumb/${TOPIC_ART[slug]}.webp`}
      alt=""
      width={420}
      height={221}
      loading="lazy"
      decoding="async"
      aria-hidden="true"
    />
  );
}

/**
 * 用語を札の形に並べる器。/learn の札 (LearnCard) と同じ型と CSS を使うので、
 * 札の並びは必ずこの器で包む。
 */
export function GlossaryTermGrid({
  children,
  list = false,
}: {
  children: ReactNode;
  /** 横長の札 (layout="row") を幅によらず 1 列に積む。置き場の幅が画面の幅と違う欄 (レッスンの左の欄) に使う */
  list?: boolean;
}) {
  return (
    <LgCardScope className={list ? "lgl-grid" : "lgl-grid lgl-grid--cards"}>{children}</LgCardScope>
  );
}

/** 一覧の項目と、関連語や学ぶ順番の項目の両方を受ける最小の形 */
export type GlossaryCardEntry = Pick<GraphqlGlossaryListItem, "slug" | "term"> & {
  shortDescription?: string | null;
  difficulty?: string | null;
} & Partial<GlossaryTopicSource>;

/**
 * 用語集で用語を並べるときの札。/learn の札と同じ型で、上に図解、分類の札、題、2 行の説明、
 * 下の行 (難易度と丸い矢印) の順。分類の札の行の右の端に、その用語の共有の絵を小さく添える。図は縦横比がまちまちなので、窓の中では切らずに収める
 * (.lgl-card--figure)。難易度の無い用語は棒を出さず、丸い矢印だけが右に残る。
 * 図の無い用語は、領域の絵を窓に置く (札の高さをそろえるため)。
 */
export function GlossaryTermCard({
  entry,
  rank,
  showTopic = true,
  external = false,
  layout = "tile",
}: {
  entry: GlossaryCardEntry;
  /** 順番つきの並び (最重要の用語) だけ渡す */
  rank?: number;
  showTopic?: boolean;
  /** レッスンの下の欄から出す札。読んでいる途中で引く場所なので、新しいタブで開く */
  external?: boolean;
  /**
   * row は絵が左、字が右の詰めた横長の札 (レッスンの下の欄)。説明を切らずに全文出し、難易度の棒は題の右に置き、
   * 下の行 (丸い矢印) と分類の札は出さない。1 列に積んでも 1 枚が 120px 前後に収まるように
   */
  layout?: "tile" | "row";
}) {
  const visual = getGlossaryVisual(entry.slug);
  const topic = getGlossaryTopic(entry as GlossaryTopicSource);
  const level = entry.difficulty ? learnLevelWord(entry.difficulty) : null;
  const art = visual ? (
    <GlossaryVisualThumb visual={visual} alt="" fetchPriority="low" imgClassName="gl-vis-img" />
  ) : (
    <GlossaryTopicArt slug={topic.slug} />
  );
  if (layout === "row") {
    return (
      <LearnCard
        to={`/glossary/${entry.slug}`}
        external={external}
        linkProps={{ prefetch: "intent" }}
        layout="row"
        className={visual ? "lgl-card--figure lgl-card--compact" : "lgl-card--compact"}
        art={art}
        tags={level ? <LgLevel level={level} /> : undefined}
        title={entry.term}
        titleMark={<TechMark name={entry.term} />}
        text={entry.shortDescription ?? undefined}
        textFull
        level={null}
        bare
      />
    );
  }
  const hasArt = Boolean(GLOSSARY_ART_IMAGES[entry.slug]);
  const hasTags = showTopic || rank !== undefined || hasArt;
  return (
    <LearnCard
      to={`/glossary/${entry.slug}`}
      external={external}
      linkProps={{ prefetch: "intent" }}
      className={visual ? "lgl-card--figure" : undefined}
      art={art}
      tags={
        hasTags ? (
          <>
            {rank !== undefined ? (
              <LgBadge>{String(rank).padStart(2, "0")}</LgBadge>
            ) : null}
            {showTopic ? <LgTag>{topic.shortLabel}</LgTag> : null}
            {/* 共有の絵と同じ絵を、札の右の端に小さく添える (図の窓は今のまま) */}
            <GlossaryArt
              slug={entry.slug}
              small
              className="-my-1 ml-auto h-auto w-16 rounded-[2px] border border-gray-light/30"
            />
          </>
        ) : undefined
      }
      title={entry.term}
        titleMark={<TechMark name={entry.term} />}
      text={entry.shortDescription ?? undefined}
      level={level}
      go={{ label: "読む" }}
    />
  );
}
